import { useState, useEffect, useContext, Suspense } from 'react';
import { MenuTitles } from 'constants/menu';
import { ContentLayout, PageLayout } from 'layout';
import {
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxLabel,
  AgxTextInput,
  AgxBodyText,
  AgxToast,
  AgxDivider,
  AgxFilterableTableHeader,
  Images,
  TableColumn,
  TableHeaderFilter,
} from '@urbanx/agx-ui-components';
import { AgxAvatarLabelGroup, AgxCheckbox, AgxSpacer } from '@urbanx/agx.ui-components2';
import { useParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { AgxToastState } from 'types/commonTypes';
import placeHolderImage from 'assets/images/placeholder-property.png';
import './Listings.scss';
import { ListingsTableColumn } from 'types/rex';
import { SetListingPublishedToPortal } from 'services';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useSelectedAgency } from 'hooks';
import {
  type CrmActiveListing,
  Provider,
  useGetCrmAgencyConnectionDetails,
  useGetCrmActiveListingsForAgency,
  useInvalidateUseGetCrmActiveListingsForAgency,
} from '@urbanx/agx.crm.client';

const ListingsPage = () => {
  const { agencyId } = useParams();
  const queryClient = useQueryClient();
  const invalidateListings = useInvalidateUseGetCrmActiveListingsForAgency();
  const [searchText, setSearchText] = useState('');
  const [shouldResetFilters, setShouldResetFilters] = useState(false);
  const { agency: selectedAgency } = useSelectedAgency();

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const { userAccount, getAuthToken } = useContext(AuthContext);

  const {
    data: connection,
    isPending: connectionPending,
    isError: connectionError,
  } = useGetCrmAgencyConnectionDetails(agencyId ?? '', Boolean(agencyId));

  const activeProvider = connection?.activeProvider;

  const canPublishToPortal = activeProvider === Provider.Rex;

  const {
    data: crmListings,
    isPending: listingsPending,
    isError: listingsReadError,
  } = useGetCrmActiveListingsForAgency(
    agencyId ?? '',
    Boolean(agencyId) && !connectionPending && !connectionError && Boolean(activeProvider)
  );

  const loadedListings = activeProvider ? crmListings : undefined;
  const isLoadingListings = connectionPending || (Boolean(activeProvider) && listingsPending);
  const listingsError = connectionError || (Boolean(activeProvider) && listingsReadError);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['getCrmActiveListingsForAgency', agencyId],
    });
  }, [agencyId, queryClient]);

  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [listings, setListings] = useState<CrmActiveListing[]>([]);
  const [filteredListings, setFilteredListings] = useState<CrmActiveListing[]>([]);
  const [filters, setFilters] = useState<{
    [id: string]: TableHeaderFilter[];
  }>({});
  const { mutate: setListingPublishedToPortal, isPending: settingPublishedToPortal } = useMutation({
    mutationFn: SetListingPublishedToPortal,
    onSuccess: () => {
      updateToastState({
        color: 'success',
        message: 'Listing publish status updated',
        open: true,
      });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error setting listing publish status',
        open: true,
      });
    },
    onSettled: () => invalidateListings(agencyId ?? ''),
  });

  useEffect(() => {
    const listings = loadedListings?.filter((listing) => {
      if (!searchText) return true;
      if (!listing.address?.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      return true;
    });

    setListings(listings ?? []);
  }, [loadedListings, searchText]);

  useEffect(() => {
    const cols: TableColumn[] = [
      {
        id: ListingsTableColumn.Image,
        name: 'Property',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Address,
        name: 'Address',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Agents,
        name: 'Agents',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Published,
        name: 'Published',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
    ];

    if (!listings) {
      setColumns(cols);
      setShouldResetFilters(true);
      setFilteredListings(listings);
      return;
    }

    const agents = listings.flatMap((listing) => listing.agents ?? []);

    cols[6].options = Array.from(new Set(agents)).map((agent) => ({
      label: agent,
      value: agent,
      selected: false,
      default: false,
    }));
    cols[9].options = [
      { label: 'Yes', value: 'Yes', selected: false, default: false },
      { label: 'No', value: 'No', selected: false, default: false },
    ];

    setColumns(cols);
    setShouldResetFilters(true);
    setFilteredListings(listings);
  }, [listings]);

  const applyFilters = (filter: { id: string; value: TableHeaderFilter[] }) => {
    const updatedFilters = { ...filters, [filter.id]: filter.value };
    setFilters(updatedFilters);

    if (!listings) {
      setFilteredListings([]);
      return;
    }

    // Check if no filters are selected
    const noFiltersSelected = Object.values(updatedFilters).every((values) =>
      values.every((v) => !v.selected)
    );

    if (noFiltersSelected) {
      setFilteredListings([...listings]);
      return;
    }

    let filteredRows = [...listings];

    Object.entries(updatedFilters).forEach(([id, values]) => {
      const selected = values.filter((v) => v.selected);
      if (selected.length === 0) return;

      if (id === ListingsTableColumn.Agents) {
        filteredRows = filteredRows.filter((row) =>
          (row.agents ?? []).some((agent) => selected.some((s) => s.value === agent))
        );
      }

      if (id === ListingsTableColumn.Published) {
        filteredRows = filteredRows.filter((row) => {
          const published = row.pushedToPortal;

          return (
            (published && selected.some((s) => s.value === 'Yes')) ||
            (!published && selected.some((s) => s.value === 'No'))
          );
        });
      }
    });

    setFilteredListings(filteredRows);
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.WEBSITE_LISTINGS}
    >
      <ContentLayout hasSecondaryMenu={true} secondaryActiveMenu={MenuTitles.WEBSITE_LISTINGS}>
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn largeGap>
          <>
            <AgxRow end>
              <AgxTextInput
                id="listingsSearch"
                label=""
                defaultValue={searchText}
                onInputValueChange={({ value }: { value: string }) => setSearchText(value)}
                noHeader
                noOptionalLabel
                placeholder="Search"
                leftIcon={<Images.SearchOutline />}
              />
            </AgxRow>
            <AgxDivider />
            <AgxRow>
              <table className="listingsTable">
                <tbody>
                  <AgxFilterableTableHeader
                    columns={columns}
                    shouldResetFilters={shouldResetFilters}
                    setShouldResetFilters={setShouldResetFilters}
                    onFilterChange={applyFilters}
                  />
                  {isLoadingListings ? (
                    <Suspense>
                      <tr>
                        <td colSpan={11}>
                          <AgxLabel large>Loading Listings...</AgxLabel>
                        </td>
                      </tr>
                    </Suspense>
                  ) : (
                    <>
                      {filteredListings?.map((listing, index) => {
                        return (
                          <tr
                            id={`listing-${listing.listingId}-${index}`}
                            key={`listing-${listing.listingId}-${index}`}
                            className="listingsRow"
                          >
                            <td colSpan={3} align="left">
                              <AgxSpacer contentAlignment="start">
                                <AgxAvatarLabelGroup
                                  id={`listing-${listing.listingId}-${index}-image`}
                                  label1=""
                                  photoUrl={listing.imageUri ?? placeHolderImage}
                                  type="photo"
                                  variant="round"
                                  size="large"
                                />
                              </AgxSpacer>
                            </td>
                            <td colSpan={3} align="left">
                              <AgxHeader size={4}>{listing.address}</AgxHeader>
                            </td>
                            <td colSpan={3} align="left">
                              <AgxBodyText small>{listing.agents?.join(', ')}</AgxBodyText>
                            </td>
                            <td colSpan={3} align="left">
                              <AgxCheckbox
                                id={`publishedCheckbox-${listing.listingId}`}
                                label={`Publish ${listing.address || listing.listingId} to portal`}
                                ariaLabelledBy={`publishedCheckbox-${listing.listingId}-label`}
                                subLabel={
                                  !canPublishToPortal
                                    ? 'Publishing to the portal is unavailable for this CRM provider.'
                                    : undefined
                                }
                                ariaDescribedBy={
                                  !canPublishToPortal
                                    ? `publishedCheckbox-${listing.listingId}-sublabel`
                                    : undefined
                                }
                                checked={Boolean(listing.pushedToPortal)}
                                disabled={!canPublishToPortal || settingPublishedToPortal}
                                onChange={(value) => {
                                  if (
                                    !canPublishToPortal ||
                                    !listing.listingId ||
                                    listing.pushedToPortal === value
                                  )
                                    return;

                                  setListingPublishedToPortal({
                                    getAuthToken,
                                    ListingId: listing.listingId,
                                    PublishToPortal: value,
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {listingsError && (
                        <tr>
                          <td colSpan={11}>
                            <div role="alert">
                              <AgxLabel large>Unable to load listings. Please try again.</AgxLabel>
                            </div>
                          </td>
                        </tr>
                      )}
                      {filteredListings.length === 0 && !isLoadingListings && !listingsError && (
                        <tr>
                          <td colSpan={11}>
                            <AgxLabel large>No Listings found</AgxLabel>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </AgxRow>
          </>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  );
};

export default ListingsPage;
